import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './RegionFormSelectDrawer.styles';
import { CloseIcon, SearchIcon } from '@/components/UI/Icons/Icons';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { Region } from '@/api/types/regions.types';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';

interface Props {
  isOpen: boolean;
  close: () => void;
  list: Region[];
  value: string;
  onSelect: (region: Region) => void;
  setQuery: (query: string) => void;
}

export const RegionFormSelectDrawer: FC<Props> = ({
  isOpen,
  close,
  list,
  value,
  onSelect,
  setQuery,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const searchInputRef = useRef<SearchInputRef>(null);

  useEffect(() => {
    searchInputRef.current?.clear();
  }, [isOpen]);

  return (
    <Drawer
      isAdaptive
      dataTestId="region-select-drawer"
      isOpen={isOpen}
      title={t('common.city')}
      left={{
        icon: (
          <CloseIcon
            width={15}
            height={15}
            fill={theme.palette.primary.labels}
          />
        ),
        onClick: close,
      }}
      hasBottomBorder={false}
      background={theme.palette.primary.background}
      subtitle={
        <S.Container>
          <SearchInput
            ref={searchInputRef}
            onSearch={setQuery}
            leftIcon={<SearchIcon fill={theme.palette.secondary.labels} />}
            hasPadding={false}
            hasBorderBottom={false}
            customPaddingTop={12}
            customPaddingBottom={12}
          />
        </S.Container>
      }
    >
      <S.Container>
        {list.length > 0 ? (
          list.map(region => {
            return (
              <Checkbox
                key={region.id}
                onChangeHook={() => onSelect(region)}
                value={region.id === value}
                label={region.name}
                appearance="radio"
                size="lg"
              />
            );
          })
        ) : (
          <NotFound>{t('common.nothing_found')}</NotFound>
        )}
      </S.Container>
    </Drawer>
  );
};

RegionFormSelectDrawer.displayName = 'RegionFormSelectDrawer';
